/**
 * Webhook Entity Types - Entities that support webhook subscriptions.
 * Each subscription is scoped to a single entity type.
 */
export enum WebhookEntityType {
  Job = 'job',
  Stage = 'stage',
  Activity = 'activity',
  Invoice = 'invoice',
  Bill = 'bill',
  Quote = 'quote',
  Opportunity = 'opportunity',
  Lead = 'lead',
  Task = 'task',
}

/**
 * Webhook Event Names - Business-level events that can trigger webhooks.
 * Grouped by entity type. These are what users see and select.
 *
 * Simplified model:
 * - Created: Entity was created
 * - Updated: Any field on entity changed
 * - StatusChanged: Status field specifically changed (use status filter to narrow)
 * - Deleted: Entity was deleted
 */
export enum WebhookEventName {
  // Job events
  JobCreated = 'Job Created',
  JobUpdated = 'Job Updated',
  JobStatusChanged = 'Job Status Changed',
  JobDeleted = 'Job Deleted',

  // Stage events
  StageCreated = 'Stage Created',
  StageUpdated = 'Stage Updated',
  StageStatusChanged = 'Stage Status Changed',
  StageDeleted = 'Stage Deleted',

  // Activity events
  ActivityCreated = 'Activity Created',
  ActivityUpdated = 'Activity Updated',
  ActivityDeleted = 'Activity Deleted',

  // Invoice events
  InvoiceCreated = 'Invoice Created',
  InvoiceUpdated = 'Invoice Updated',
  InvoiceStatusChanged = 'Invoice Status Changed',
  InvoiceDeleted = 'Invoice Deleted',

  // Bill events
  BillCreated = 'Bill Created',
  BillUpdated = 'Bill Updated',
  BillStatusChanged = 'Bill Status Changed',
  BillDeleted = 'Bill Deleted',

  // Quote events
  QuoteCreated = 'Quote Created',
  QuoteUpdated = 'Quote Updated',
  QuoteStatusChanged = 'Quote Status Changed',
  QuoteDeleted = 'Quote Deleted',

  // Opportunity events
  OpportunityCreated = 'Opportunity Created',
  OpportunityUpdated = 'Opportunity Updated',
  OpportunityStatusChanged = 'Opportunity Status Changed',
  OpportunityDeleted = 'Opportunity Deleted',

  // Lead events
  LeadCreated = 'Lead Created',
  LeadUpdated = 'Lead Updated',
  LeadStatusChanged = 'Lead Status Changed',
  LeadDeleted = 'Lead Deleted',

  // Task events
  TaskCreated = 'Task Created',
  TaskUpdated = 'Task Updated',
  TaskStatusChanged = 'Task Status Changed',
  TaskDeleted = 'Task Deleted',
}

/**
 * Maps entity types to their available webhook events.
 * Consistent pattern: Created, Updated, StatusChanged, Deleted
 */
export const WebhookEntityEvents: Record<WebhookEntityType, WebhookEventName[]> = {
  [WebhookEntityType.Job]: [
    WebhookEventName.JobCreated,
    WebhookEventName.JobUpdated,
    WebhookEventName.JobStatusChanged,
    WebhookEventName.JobDeleted,
  ],
  [WebhookEntityType.Stage]: [
    WebhookEventName.StageCreated,
    WebhookEventName.StageUpdated,
    WebhookEventName.StageStatusChanged,
    WebhookEventName.StageDeleted,
  ],
  [WebhookEntityType.Activity]: [
    WebhookEventName.ActivityCreated,
    WebhookEventName.ActivityUpdated,
    WebhookEventName.ActivityDeleted,
  ],
  [WebhookEntityType.Invoice]: [
    WebhookEventName.InvoiceCreated,
    WebhookEventName.InvoiceUpdated,
    WebhookEventName.InvoiceStatusChanged,
    WebhookEventName.InvoiceDeleted,
  ],
  [WebhookEntityType.Bill]: [
    WebhookEventName.BillCreated,
    WebhookEventName.BillUpdated,
    WebhookEventName.BillStatusChanged,
    WebhookEventName.BillDeleted,
  ],
  [WebhookEntityType.Quote]: [
    WebhookEventName.QuoteCreated,
    WebhookEventName.QuoteUpdated,
    WebhookEventName.QuoteStatusChanged,
    WebhookEventName.QuoteDeleted,
  ],
  [WebhookEntityType.Opportunity]: [
    WebhookEventName.OpportunityCreated,
    WebhookEventName.OpportunityUpdated,
    WebhookEventName.OpportunityStatusChanged,
    WebhookEventName.OpportunityDeleted,
  ],
  [WebhookEntityType.Lead]: [
    WebhookEventName.LeadCreated,
    WebhookEventName.LeadUpdated,
    WebhookEventName.LeadStatusChanged,
    WebhookEventName.LeadDeleted,
  ],
  [WebhookEntityType.Task]: [
    WebhookEventName.TaskCreated,
    WebhookEventName.TaskUpdated,
    WebhookEventName.TaskStatusChanged,
    WebhookEventName.TaskDeleted,
  ],
};

/**
 * Maps webhook event names to their entity type.
 */
export const WebhookEventToEntity: Record<WebhookEventName, WebhookEntityType> = Object.entries(WebhookEntityEvents).reduce((acc, [entity, events]) => {
  events.forEach(event => { acc[event] = entity as WebhookEntityType; });
  return acc;
}, {} as Record<WebhookEventName, WebhookEntityType>);

/**
 * Labels for entity types (for UI display).
 */
export const WebhookEntityLabels: Record<WebhookEntityType, string> = {
  [WebhookEntityType.Job]: 'Jobs',
  [WebhookEntityType.Stage]: 'Stages',
  [WebhookEntityType.Activity]: 'Activities',
  [WebhookEntityType.Invoice]: 'Invoices',
  [WebhookEntityType.Bill]: 'Bills',
  [WebhookEntityType.Quote]: 'Quotes',
  [WebhookEntityType.Opportunity]: 'Opportunities',
  [WebhookEntityType.Lead]: 'Leads',
  [WebhookEntityType.Task]: 'Tasks',
};
